import { useState, useEffect, useCallback, useRef } from 'react'
import { adaloBackendAxios } from 'utils/io/http/axios'
import { useFunnelTracking } from 'hooks/useFunnelTracking'

export const LOADING = 'loading'
export const FAILURE = 'failure'

const useSheetBridgeFlow = ({ appId, organizationId }) => {
  const [step, setStep] = useState(0)
  const [bridges, setBridges] = useState([])
  const [bridgesStatus, setBridgesStatus] = useState('idle')
  const [authState, setAuthState] = useState('idle')
  const [checkingAuth, setCheckingAuth] = useState(true)
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [noBridges, setNoBridges] = useState(false)
  const timedOutRef = useRef(false)

  const { trackClickedSheetBridge, trackSheetBridgeGotBridges } =
    useFunnelTracking()

  useEffect(() => {
    const checkAuth = async () => {
      try {
        setCheckingAuth(true)
        const sessionId = localStorage.getItem('sheetbridge_session_id')
        const { data } = await adaloBackendAxios.get(
          `/external-databases/sheetbridge/auth-status`,
          {
            params: { appId },
            headers: sessionId ? { 'X-SheetBridge-Session': sessionId } : {},
            timeout: 5000,
          }
        )
        setIsAuthenticated(data.authenticated)
      } catch (error) {
        console.warn('[SheetBridge] Auth status check failed:', error.message)
        setIsAuthenticated(false)
      } finally {
        setCheckingAuth(false)
      }
    }

    checkAuth()
  }, [appId])

  const fetchBridges = useCallback(async () => {
    setBridgesStatus('loading')

    try {
      const sessionId = localStorage.getItem('sheetbridge_session_id')
      const { data } = await adaloBackendAxios.get(
        `/external-databases/sheetbridge/bridges`,
        {
          params: { appId },
          headers: sessionId ? { 'x-sheetbridge-session': sessionId } : {},
          timeout: 10000,
        }
      )

      const bridgesData = data.bridges || []
      const totalSheets = bridgesData.reduce(
        (sum, bridge) => sum + (bridge.sheets?.length || 0),
        0
      )

      if (totalSheets === 0) {
        setNoBridges(true)
        setIsAuthenticated(true)
        setAuthState('idle')
        setBridgesStatus('idle')
        setStep(0)

        return
      }

      setBridges(bridgesData)
      setBridgesStatus('loaded')
      setIsAuthenticated(true)
      setNoBridges(false)
      setAuthState('idle')
      setStep(1)

      if (organizationId) {
        trackSheetBridgeGotBridges(organizationId).catch(error => {
          console.error('Failed to track SheetBridge got bridges:', error)
        })
      }
    } catch (error) {
      console.error('[SheetBridge] Error fetching bridges:', error)
      setBridgesStatus('error')
      setAuthState(FAILURE)
    }
  }, [appId, organizationId, trackSheetBridgeGotBridges])

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    const oauthSuccess = urlParams.get('sheetbridge_oauth') === 'success'
    const sessionId = urlParams.get('session_id')

    if (!oauthSuccess) return

    console.log('[SheetBridge] OAuth success detected')

    if (sessionId) {
      localStorage.setItem('sheetbridge_session_id', sessionId)
    } else {
      console.error('[SheetBridge] No session ID in OAuth callback URL')
    }

    window.history.replaceState({}, '', window.location.pathname)

    timedOutRef.current = false

    const timeout = setTimeout(() => {
      timedOutRef.current = true
      console.error('[SheetBridge] Timeout: bridges fetch took too long')
      setAuthState(FAILURE)
      setCheckingAuth(false)
      setStep(0)
    }, 10000)

    fetchBridges()
      .catch(err => {
        if (timedOutRef.current) return
        console.error('[SheetBridge] Error in OAuth callback:', err)
        setAuthState(FAILURE)
        setStep(0)
      })
      .finally(() => {
        clearTimeout(timeout)
        if (!timedOutRef.current) setCheckingAuth(false)
      })
  }, [fetchBridges])

  const handleGetBridges = useCallback(async () => {
    try {
      setAuthState(LOADING)

      if (organizationId) {
        trackClickedSheetBridge(organizationId).catch(error => {
          console.error('Failed to track SheetBridge click:', error)
        })
      }

      // If the user already has a valid SheetBridge session, fetch their
      // bridges directly instead of forcing them through OAuth again.
      if (isAuthenticated) {
        await fetchBridges()

        return
      }

      const { data } = await adaloBackendAxios.get(
        `/external-databases/sheetbridge/auth-url`,
        {
          params: { appId },
          timeout: 5000,
        }
      )

      if (data?.authUrl) {
        window.location.href = data.authUrl
      } else {
        throw new Error('Failed to get authentication URL')
      }
    } catch (error) {
      console.error('[SheetBridge] OAuth redirect failed:', error)
      setAuthState(FAILURE)
    }
  }, [
    appId,
    organizationId,
    trackClickedSheetBridge,
    isAuthenticated,
    fetchBridges,
  ])

  const goBack = useCallback(() => {
    if (step > 0) setStep(step - 1)
  }, [step])

  return {
    step,
    bridges,
    bridgesStatus,
    authState,
    checkingAuth,
    isAuthenticated,
    noBridges,
    handleGetBridges,
    goBack,
  }
}

export default useSheetBridgeFlow
